import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Turnstile } from "@marsidev/react-turnstile";
import { Wine, Lock, User, ArrowRight, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITEKEY = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITEKEY;

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/invitations/preview/${token}`);
        setInfo(data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Invitation not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      toast.error("Please complete the bot check.");
      return;
    }
    setBusy(true);
    const r = await acceptInvite(token, name, password, turnstileToken);
    setBusy(false);
    if (!r.ok) {
      setTurnstileToken("");
      setTurnstileKey((k) => k + 1);
      toast.error(r.error || "Could not accept invitation");
      return;
    }
    if (r.mfaSetupRequired) {
      toast.success(`Welcome to ${info?.org_name || "LiterallyDrink"}`);
      toast.message("Set up two-factor authentication to finish.");
      navigate("/mfa/setup");
      return;
    }
    toast.success(`Welcome to ${info?.org_name || "LiterallyDrink"}`);
    navigate("/");
  };

  return (
    <div className="min-h-screen login-canvas grain overflow-hidden">
      <div className="relative z-10 max-w-[560px] mx-auto px-4 py-12 min-h-screen flex flex-col items-center justify-center fade-in">
        <div className="flex items-center gap-3 mb-6">
          <img
            src="/literally-logo.png"
            alt="Literally Vodka"
            className="w-11 h-11 rounded-lg object-cover shadow-soft"
          />
          <div>
            <div className="font-display text-lg text-brand-ink">LiterallyDrink</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-mutedFg">Sales Tracker</div>
          </div>
        </div>

        <div className="card p-8 sm:p-10 w-full" data-testid="invite-accept-card">
          {loading ? (
            <div className="text-brand-mutedFg text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Validating invitation…</div>
          ) : error ? (
            <div>
              <div className="label">Invitation problem</div>
              <h2 className="font-display text-3xl text-brand-ink mt-1">We couldn't open that invite</h2>
              <p className="text-sm text-brand-mutedFg mt-3">{error}</p>
              <Link to="/login" className="btn-outline mt-5 inline-flex">Back to sign in</Link>
            </div>
          ) : (
            <>
              <div className="label">You've been invited</div>
              <h2 className="font-display text-3xl text-brand-ink mt-1">
                Join <span className="italic text-brand-gold">{info?.org_name || "the team"}</span>
              </h2>
              <div className="mt-3 text-sm text-brand-mutedFg flex items-center gap-2">
                <ShieldCheck size={14} className="text-brand-gold" />
                <span>
                  Role: <strong className="text-brand-ink capitalize">{info?.role}</strong>
                  {info?.team_name && <> · Team: <strong className="text-brand-ink">{info.team_name}</strong></>}
                </span>
              </div>
              {info?.email && (
                <div className="mt-1 text-xs text-brand-mutedFg">Email: {info.email}</div>
              )}

              <form onSubmit={submit} className="mt-6 flex flex-col gap-3" data-testid="invite-form">
                <label className="block">
                  <span className="label">Your name</span>
                  <div className="relative mt-1">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
                    <input
                      data-testid="invite-name-input"
                      required
                      className="input-base pl-10"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jane Sommelier"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="label">Create a password</span>
                  <div className="relative mt-1">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
                    <input
                      data-testid="invite-password-input"
                      required
                      type="password"
                      minLength={8}
                      className="input-base pl-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters incl. a number"
                    />
                  </div>
                </label>

                <div className="mt-2 flex justify-center" data-testid="invite-turnstile">
                  {SITEKEY ? (
                    <Turnstile
                      key={turnstileKey}
                      siteKey={SITEKEY}
                      onSuccess={setTurnstileToken}
                      onError={() => setTurnstileToken("")}
                      onExpire={() => setTurnstileToken("")}
                      options={{ theme: "light", size: "flexible" }}
                    />
                  ) : (
                    <div className="text-xs text-stage-lostFg">Bot check is not configured.</div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={busy || !turnstileToken}
                  className="btn-primary mt-2 group"
                  data-testid="invite-submit-button"
                >
                  {busy ? (<><Loader2 size={16} className="animate-spin"/> Setting up…</>) : (<>Accept & continue <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" /></>)}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
